import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Store, MessageSquare, BookOpen, ArrowRight, Plus, Coffee, Users,
  AlertTriangle, CheckCircle2, Clock, DollarSign, Wrench, MapPin,
  CalendarClock, ShoppingCart, FolderOpen, TrendingUp, BarChart3, Briefcase,
  Wallet, Flame, Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00");
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try { const res = await axios.get(`${API}/dashboard/stats`); setStats(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const overdueCount = stats?.overdue_tasks?.length || 0;
  const taskProgress = stats?.tasks_count > 0 ? Math.round((stats.tasks_complete / stats.tasks_count) * 100) : 0;
  const fmt = (n) => `$${(n || 0).toLocaleString()}`;

  return (
    <div className="min-h-screen" data-testid="dashboard">
      {/* Hero */}
      <div className="px-6 md:px-12 lg:px-16 pt-10 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
            <Coffee className="w-5 h-5 text-stone-50" />
          </div>
          <span className="text-xs font-medium uppercase tracking-widest text-stone-500">Business Command Center</span>
        </div>
        <h1 data-testid="dashboard-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          Dashboard
        </h1>
        <p className="text-base text-stone-500 max-w-xl leading-relaxed">
          Your coffee empire at a glance — stores, tasks, equipment, team, and more.
        </p>
      </div>

      <div className="px-6 md:px-12 lg:px-16 pb-16">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
          {[
            { label: "Stores", value: stats?.stores_count || 0, icon: Store, color: "bg-amber-50 text-amber-700", link: "/stores" },
            { label: "Staff", value: stats?.staff_count || 0, sub: `${stats?.staff_active || 0} active`, icon: Users, color: "bg-blue-50 text-blue-700", link: "/staff" },
            { label: "Open Roles", value: stats?.open_roles || 0, sub: `${stats?.total_candidates || 0} candidates`, icon: Briefcase, color: "bg-violet-50 text-violet-700", link: "/staff" },
            { label: "Tasks", value: stats?.tasks_count || 0, sub: `${taskProgress}% done`, icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700", link: null },
            { label: "Revenue", value: fmt(stats?.total_revenue), isText: true, icon: TrendingUp, color: "bg-emerald-50 text-emerald-700", link: "/financials" },
            { label: "Net Profit", value: fmt(stats?.net_profit), isText: true, icon: DollarSign, color: (stats?.net_profit || 0) >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600", link: "/financials" },
            { label: "Equipment", value: stats?.equipment_count || 0, icon: Wrench, color: "bg-stone-100 text-stone-700", link: "/equipment" },
            { label: "Documents", value: stats?.documents_count || 0, icon: FolderOpen, color: "bg-cyan-50 text-cyan-700", link: "/documents" },
          ].map((kpi, idx) => (
            <Card
              key={kpi.label}
              data-testid={`kpi-${kpi.label.toLowerCase()}`}
              className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl cursor-pointer animate-slide-up"
              style={{ animationDelay: `${idx * 0.05}s` }}
              onClick={() => kpi.link && navigate(kpi.link)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${kpi.color}`}>
                    <kpi.icon className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-semibold text-stone-900">{loading ? "—" : kpi.isText ? kpi.value : kpi.value}</p>
                <p className="text-xs text-stone-500 mt-0.5">{kpi.label}</p>
                {kpi.sub && <p className="text-xs text-stone-400">{kpi.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Alerts & Financial Row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
          {/* Overdue Alert */}
          <Card className={`border rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] ${overdueCount > 0 ? "border-red-200 bg-red-50/50" : "border-stone-200/60 bg-white"}`} data-testid="overdue-alert">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className={`w-4 h-4 ${overdueCount > 0 ? "text-red-600" : "text-stone-400"}`} />
                <h3 className={`text-sm font-semibold ${overdueCount > 0 ? "text-red-800" : "text-stone-700"}`}>Overdue Tasks</h3>
                {overdueCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded-full">{overdueCount}</span>
                )}
              </div>
              {overdueCount > 0 ? (
                <div className="space-y-2">
                  {stats.overdue_tasks.slice(0, 4).map((t) => (
                    <div key={t.id} className="flex items-center justify-between text-sm">
                      <span className="text-red-900 truncate flex-1">{t.title}</span>
                      <span className="text-xs text-red-600 ml-2 flex-shrink-0">Due {formatDate(t.due_date)}</span>
                    </div>
                  ))}
                  {overdueCount > 4 && <p className="text-xs text-red-500">+{overdueCount - 4} more</p>}
                </div>
              ) : (
                <p className="text-sm text-stone-500">No overdue tasks — you're on track!</p>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="financial-summary">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-stone-700">Equipment Costs</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-stone-500">Total Equipment</span>
                  <span className="text-lg font-semibold text-stone-900">${(stats?.total_equipment_cost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-stone-500">Pending Purchases</span>
                  <span className="text-base font-medium text-amber-700">${(stats?.pending_equipment_cost || 0).toLocaleString()}</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2 mt-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${stats?.total_equipment_cost > 0 ? Math.round(((stats.total_equipment_cost - (stats.pending_equipment_cost || 0)) / stats.total_equipment_cost) * 100) : 0}%` }}
                  />
                </div>
                <p className="text-xs text-stone-400">Purchased vs pending</p>
              </div>
            </CardContent>
          </Card>

          {/* Task Progress */}
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="task-progress">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-stone-700">Task Progress</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-stone-500">Completed</span>
                  <span className="text-lg font-semibold text-stone-900">{stats?.tasks_complete || 0} / {stats?.tasks_count || 0}</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${taskProgress}%` }} />
                </div>
                <div className="flex justify-between text-xs text-stone-400">
                  <span>{stats?.tasks_in_progress || 0} in progress</span>
                  <span>{taskProgress}% complete</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Labor Cost */}
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] cursor-pointer card-hover" data-testid="labor-cost-card" onClick={() => navigate("/financials")}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-stone-700">Financial Health</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-stone-500">Labor Cost %</span>
                  <span className="text-lg font-semibold text-stone-900">{stats?.labor_pct || 0}%</span>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${(stats?.labor_pct || 0) > 35 ? "bg-red-500" : (stats?.labor_pct || 0) > 25 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(stats?.labor_pct || 0, 100)}%` }} />
                </div>
                <p className="text-xs text-stone-400">{(stats?.labor_pct || 0) <= 30 ? "Healthy range" : "Above industry average (25-30%)"}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Stores Overview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Stores
              </h2>
              <Button variant="ghost" size="sm" className="text-stone-500 hover:text-stone-900" onClick={() => navigate("/stores")} data-testid="view-all-stores-btn">
                View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {stats?.stores?.length > 0 ? (
                stats.stores.slice(0, 5).map((store) => {
                  const days = daysUntil(store.target_open_date);
                  const isOverdue = days !== null && days < 0 && store.status !== "open";
                  return (
                    <Card key={store.id} data-testid={`dash-store-${store.id}`} className="card-hover bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl cursor-pointer" onClick={() => navigate(`/stores/${store.id}/timeline`)}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Store className="w-4 h-4 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{store.name}</p>
                          <p className="text-xs text-stone-400">
                            {store.city && `${store.city}, ${store.state}`}
                            {store.status && ` · ${store.status}`}
                          </p>
                        </div>
                        {store.target_open_date && (
                          <span className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0 ${
                            isOverdue ? "bg-red-100 text-red-700" : days !== null && days <= 30 ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"
                          }`}>
                            <CalendarClock className="w-3 h-3" />
                            {isOverdue ? `${Math.abs(days)}d overdue` : days !== null ? `${days}d left` : formatDate(store.target_open_date)}
                          </span>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Card className="bg-white border border-stone-200/60 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <Store className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No stores yet</p>
                    <Button data-testid="first-store-btn" onClick={() => navigate("/stores")} variant="ghost" size="sm" className="mt-2">
                      Add first store <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Upcoming Tasks */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Upcoming Tasks
              </h2>
            </div>
            <div className="space-y-2">
              {stats?.upcoming_tasks?.length > 0 ? (
                stats.upcoming_tasks.slice(0, 5).map((task) => {
                  const days = daysUntil(task.due_date);
                  return (
                    <Card key={task.id} data-testid={`dash-task-${task.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          task.status === "in_progress" ? "bg-blue-50" : "bg-stone-100"
                        }`}>
                          <Clock className={`w-4 h-4 ${task.status === "in_progress" ? "text-blue-600" : "text-stone-500"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{task.title}</p>
                          <p className="text-xs text-stone-400">{task.category}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-lg flex-shrink-0 ${
                          days !== null && days <= 3 ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"
                        }`}>
                          {formatDate(task.due_date)}
                        </span>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Card className="bg-white border border-stone-200/60 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <CheckCircle2 className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No upcoming tasks in the next 14 days</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Purchases */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Upcoming Purchases
              </h2>
              <Button variant="ghost" size="sm" className="text-stone-500 hover:text-stone-900" onClick={() => navigate("/equipment")} data-testid="view-all-equipment-btn">
                View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {stats?.upcoming_purchases?.length > 0 ? (
                stats.upcoming_purchases.slice(0, 5).map((eq) => (
                  <Card key={eq.id} data-testid={`dash-purchase-${eq.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl cursor-pointer card-hover" onClick={() => navigate("/equipment")}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ShoppingCart className="w-4 h-4 text-stone-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">{eq.name}</p>
                        <p className="text-xs text-stone-400">
                          {eq.brand && `${eq.brand} · `}{eq.status}
                          {eq.lead_time_days && ` · ${eq.lead_time_days}d lead`}
                        </p>
                      </div>
                      {eq.cost != null && (
                        <span className="text-sm font-medium text-stone-700 flex-shrink-0">
                          ${(eq.cost * (eq.quantity || 1)).toLocaleString()}
                        </span>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="bg-white border border-stone-200/60 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <ShoppingCart className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No pending equipment purchases</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Hiring Pipeline */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Hiring Pipeline
              </h2>
              <Button variant="ghost" size="sm" className="text-stone-500 hover:text-stone-900" onClick={() => navigate("/staff")} data-testid="view-all-staff-btn">
                View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="space-y-2">
              {stats?.hiring_pipeline?.length > 0 ? (
                stats.hiring_pipeline.slice(0, 5).map((s) => (
                  <Card key={s.id} data-testid={`dash-hire-${s.id}`} className="bg-white border border-stone-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)] rounded-xl cursor-pointer card-hover" onClick={() => navigate("/staff")}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-blue-700">{s.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">{s.name}</p>
                        <p className="text-xs text-stone-400">{s.role || s.department}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        s.status === "hiring" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                      }`}>{s.status}</span>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="bg-white border border-stone-200/60 rounded-xl">
                  <CardContent className="p-8 text-center">
                    <Users className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No open positions in pipeline</p>
                    <Button data-testid="first-hire-btn" onClick={() => navigate("/staff")} variant="ghost" size="sm" className="mt-2">
                      Add team member <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-3 mb-8">
          <Button data-testid="quick-consult-btn" onClick={() => navigate("/chat")} className="bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg shadow-stone-900/10 active:scale-95 transition-all font-medium px-6 btn-scale">
            <MessageSquare className="w-4 h-4 mr-2" /> AI Consultant
          </Button>
          <Button data-testid="quick-add-store-btn" onClick={() => navigate("/stores")} variant="outline" className="border-stone-200 hover:bg-stone-50 shadow-sm active:scale-95 transition-all font-medium px-6 btn-scale">
            <Plus className="w-4 h-4 mr-2" /> Add Store
          </Button>
          <Button data-testid="quick-accounts-btn" onClick={() => navigate("/accounts")} variant="outline" className="border-stone-200 hover:bg-stone-50 shadow-sm active:scale-95 transition-all font-medium px-6 btn-scale">
            <Wallet className="w-4 h-4 mr-2" /> Accounts
          </Button>
          <Button data-testid="quick-upload-btn" onClick={() => navigate("/documents")} variant="outline" className="border-stone-200 hover:bg-stone-50 shadow-sm active:scale-95 transition-all font-medium px-6 btn-scale">
            <FolderOpen className="w-4 h-4 mr-2" /> Upload Docs
          </Button>
        </div>

        {/* Activity Feed + Cash Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Feed */}
          <div>
            <h2 className="text-lg font-medium text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Recent Activity
            </h2>
            {stats?.recent_activity?.length > 0 ? (
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="activity-feed">
                <CardContent className="p-4 space-y-3">
                  {stats.recent_activity.slice(0, 8).map((a, i) => (
                    <div key={a.id} className="flex items-start gap-3 animate-slide-up" style={{ animationDelay: `${i * 0.03}s` }}>
                      <div className="w-7 h-7 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Activity className="w-3.5 h-3.5 text-stone-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-stone-700">
                          <span className="font-medium">{a.user_name}</span>{" "}
                          <span className="text-stone-500">{a.action}</span>{" "}
                          <span className="font-medium">{a.entity_name}</span>
                        </p>
                        <p className="text-xs text-stone-400">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border border-stone-200/60 rounded-xl">
                <CardContent className="p-8 text-center">
                  <Activity className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                  <p className="text-sm text-stone-500">Activity will appear as you and your team take actions.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cash & Burn Summary */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                Cash Position
              </h2>
              <Button variant="ghost" size="sm" className="text-stone-500 hover:text-stone-900" onClick={() => navigate("/accounts")} data-testid="view-accounts-btn">
                Full Accounts <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] cursor-pointer card-hover" onClick={() => navigate("/accounts")} data-testid="cash-position-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-stone-500">Cash Position</span>
                  </div>
                  <p className={`text-3xl font-semibold ${(stats?.cash_position || 0) >= 0 ? "text-emerald-800" : "text-red-700"}`}>
                    ${(stats?.cash_position || 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] cursor-pointer card-hover" onClick={() => navigate("/accounts")} data-testid="burn-rate-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className="w-4 h-4 text-amber-600" />
                    <span className="text-xs text-stone-500">Weekly Burn Rate</span>
                  </div>
                  <p className="text-3xl font-semibold text-stone-900">
                    ${(stats?.weekly_burn || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-stone-400 mt-1">avg over last 12 weeks</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
